export type AnnotationType = "text" | "sketch" | "measurement";

/** World-space position of an anchored-annotation marker (Unity world space). */
export type AnnotationVec3 = readonly [number, number, number];
/** Marker orientation quaternion (mostly moot — the marker billboards). */
export type AnnotationQuat = readonly [number, number, number, number];

/** Stable per-author avatar color (hashed hue) — shared by the 3D marker
 *  bubble and the inline editor so the avatar looks consistent. */
export function annotationAuthorColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return `hsl(${Math.abs(hash) % 360}, 60%, 52%)`;
}

export interface AnnotationAuthor {
  readonly id: string;
  readonly name: string;
  readonly initials: string;
  /**
   * Unity `CreatorNaturalAccountId` — the author's account (customer) id,
   * stamped for cross-platform thread coherence. Optional: not needed for
   * display, only carried through so cloud writes round-trip Unity's creator
   * shape. Undefined for in-memory / offline authors.
   */
  readonly naturalAccountId?: string;
}

/**
 * The signed-in user as an annotation author, with the same fallbacks the
 * Comment tool, the Sketch tool and the comments list each spell out inline.
 */
export function annotationAuthorFromUser(
  user: {
    readonly userId: string;
    readonly displayName?: string | null;
    readonly email?: string | null;
    readonly customerId?: string | null;
  } | null,
): AnnotationAuthor {
  const name = user?.displayName || user?.email || "You";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initials = parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : (parts[0]?.slice(0, 2) || "U").toUpperCase();
  return {
    id: user?.userId ?? "local-user",
    name,
    initials,
    naturalAccountId: user?.customerId ?? undefined,
  };
}

export interface AnnotationReplyData {
  readonly id: string;
  readonly parentId: string;
  readonly message: string;
  readonly creator: AnnotationAuthor;
  readonly createdAt: string;
  readonly modifiedAt: string;
}

export interface AnnotationData {
  readonly id: string;
  readonly sceneId: string;
  readonly type: AnnotationType;
  readonly text: string;
  readonly creator: AnnotationAuthor;
  readonly createdAt: string;
  readonly modifiedAt: string;
  readonly isResolved: boolean;
  readonly isDetached: boolean;
  readonly replies: ReadonlyArray<AnnotationReplyData>;

  /**
   * Anchored-marker geometry. Present when the annotation was placed in the
   * 3D scene with the Comment tool; absent for detached list-only comments
   * (the panel "New comment" box). All Unity world space.
   *   - `attachmentPosition` — the anchor on the surface (bezier start). Fixed
   *     in Unity; on web the user can drag it to another surface, which is how
   *     a comment moves to another part (its part is read off that surface).
   *   - `placementPosition` — the draggable marker (bezier end / bubble).
   *   - `placementRotation` — marker orientation (billboard overrides facing).
   *
   * Web-port deviation vs Unity: Unity stores these in the anchored part's
   * LOCAL space (so the marker follows a moving part) and sets
   * `ParentEntitySnapshotId`. The web stores WORLD space with an empty parent
   * so the bezier pins to the exact surface world point and the row never
   * renders a mis-placed marker in the Unity client (Unity treats empty-parent
   * text as list-only). A part-local + handedness bake is a follow-up.
   */
  readonly attachmentPosition?: AnnotationVec3;
  readonly placementPosition?: AnnotationVec3;
  readonly placementRotation?: AnnotationQuat;
  /**
   * Unity `ParentEntitySnapshotId` — set only on a comment a desktop client
   * attached to a part, whose positions above are then in THAT PART's local
   * space, not world space. Absent on every web-written row. Read it rather
   * than `isDetached`, which the load path and the live-event path derive
   * differently (parent id vs placement pose).
   */
  readonly parentSnapshotId?: string;

  /**
   * Sketch-only fields (`type === "sketch"`). A sketch annotation is a
   * screenshot-annotate artifact: the row's placement pose is the CAMERA pose
   * at capture time and the drawn layers live in S3 (`Sketchs/{id}*.png`).
   *   - `sketchUploaded` — Unity's validity gate: the row is created BEFORE
   *     the PNGs exist and stays hidden everywhere until this flips true
   *     (`AnnotationsLayerModel.IsValid`). Absent on text comments.
   *   - `fov` / `orthographicSize` — capture camera framing (Fov 0 ⇒ the
   *     capture camera was orthographic).
   */
  readonly sketchUploaded?: boolean;
  readonly fov?: number;
  readonly orthographicSize?: number;
}
